/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './passphrase.module.css' with {type: 'css'};
import html from './passphrase.module.html';

import { toInt } from '../instruments';


const EMOJIS = '😀🦕🔥🥶🪐🐶🚌🌎🦋😡👽🍁🦊🌻🍏🐠';

const OTHERS = [
    '😂','😇','😉','🤪','🥸',,'☹️','😡','🤯',
    '😱','🙄','😵‍💫','🤠','😺',
    '🧠','👋','✌️','🕶','🐨','🐯','🦁',
    ,'🐔','🐴','🦄','🐝','🦋','🐢','🧐','🐙',
    '🦀','🐋','🦓','🦧','🐓','🐷',
    '🌈','☁️','👍','👊','🙃','😍','😛','🥹','😅',
    '😫','🥺',
];

/**
 * Passphrase authenticator using emoji selections.
 * ```html
 * <pass-phrase digits="6"></dialog>
 * ```
 * 
 * When the user enters all of the emojis, the element will emit a custom event 'passphrase-entered'
 */
export class Passphrase extends HTMLElement {

    static readonly ELEMENT = "pass-phrase";

    static DefineElement() {
        if (customElements.get(Passphrase.ELEMENT) === undefined) {
            customElements.define(Passphrase.ELEMENT, Passphrase);
        }
    }

    static observedAttributes = [ 
        "digits",   // how many digits to enter (e.g. "6")
        "charset",  // string of 16 unicode characters that representing the hex digits 0-F
        "loading",  // when "true" show loading message overlay
        "success",  // when "true" show success icon
        "fail"      // when "true" show failure icon
    ];

    private root : ShadowRoot;

    private charset : string[] = [];


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
    }

    connectedCallback() {
        for (const c of this.getAttribute('charset') ?? EMOJIS) this.charset.push(c);
        this.render();
        document.addEventListener('keydown', this.handleKeypress);
    }

    disconnectedCallback() {
        document.removeEventListener('keydown', this.handleKeypress);
    }

    /**
     * When an attribute is changed on our custom component, this gets fired...
     */
    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (name === 'loading') {
            this.root.querySelector('#passphrase')?.classList.toggle('loading', (newValue === 'true'));
        } 
        else if (name === 'success') {
            this.root.querySelector('#passphrase')?.classList.toggle('success', (newValue === 'true'));
        }
        else if (name === 'fail') {
            this.root.querySelector('#passphrase')?.classList.toggle('fail', (newValue === 'true'));
        }
    }


    private render() {
        const digits = toInt(this.getAttribute('digits'), 5);
        const message = this.root.querySelector('h1.welcome') as HTMLElement;
        //message.innerHTML = message.innerHTML.replace('{{username}}', this.getAttribute('username') || 'back');

        const container = this.root.querySelector('.digits')!;
        container.innerHTML = '';
        for (let i=0; i<digits; i++) {
            const digit = document.createElement('div');
            digit.classList.add('digit');
            digit.setAttribute('tabindex', `${i}`);
            digit.addEventListener('click', e => { this.setFocus(i) });
            digit.innerHTML = '?';
            container.append(digit);
            if (i === 0) digit.classList.add('active');
        }

        const emojis = this.root.querySelector('.emojis')!;

        let row = document.createElement('div');
        row.classList.add('row');
        for (let i=0; i<16; i++) {
            const emoji = document.createElement('div');
            emoji.classList.add('emoji');
            emoji.addEventListener('pointerenter', e => {
                this.setHTML('.digit.active', this.charset[i]);
            });
            emoji.addEventListener('pointerleave', e => { 
                this.setHTML('.digit.active', '?')
            });
            emoji.addEventListener('click', e => {
                this.setHTML('.digit.active', this.charset[i]);
                this.advanceFocus();
            });
            emoji.innerHTML = this.charset[i];
            row.append(emoji);
            if (i === 7 || i === 15) emojis.append(row);
            if (i === 7) {
                row = document.createElement('div');
                row.classList.add('row');
            }
        }
    }

    public reset() {
        this.root.querySelectorAll('.digit').forEach(d => d.innerHTML = '?');
        this.setFocus(0);
    }


    private handleKeypress = (e : KeyboardEvent) => {
        let key = '';
        if (this.charset.includes(e.key)) {
            key = e.key;
        } else if (this.charset.includes(e.key.toUpperCase())) {
            key = e.key.toUpperCase();
        } else if (this.charset.includes(e.key.toLowerCase())) {
            key = e.key.toLowerCase();
        }
        if (this.charset.includes(key)) {
            this.setHTML('.digit.active', key);
            this.advanceFocus();
        }
    }


    private async advanceFocus() {
        const digits = this.root.querySelectorAll('.digit').length;
        const el = this.root.querySelector('.digit.active');
        const index = toInt(el?.getAttribute('tabindex'), 0);
        if (this.isComplete()) {
            this.emitEnterEvent();
        }
        (index + 1 >= digits) ? this.setFocus(0) : this.setFocus(index + 1);
    }

    private setFocus(index : number) {
        this.root.querySelectorAll('.digit').forEach(d => { d.classList.remove('active'); });
        const el = this.root.querySelector(`.digit[tabindex="${index}"]`);
        el?.classList.add('active');
        if (el) el.innerHTML = '?';
    }

    private setHTML(selector : string, content : string) {
        const el = this.root.querySelector(selector);
        if (el) el.innerHTML = content;
    }

    private isComplete() : boolean {
        let complete = true;
        this.root.querySelectorAll('.digit').forEach(d => {
            if (d.innerHTML === '?') {
                complete = false;
                return;
            }
        });
        return complete;
    }

    private async emitEnterEvent() : Promise<boolean> {

        let code = '', text = '';

        this.root.querySelectorAll('.digit').forEach(el => {
            const emoji = el.innerHTML;
            let digit = this.charset.indexOf(emoji);
            code += digit.toString(16);
            text += emoji;
        });

        this.dispatchEvent(
            new CustomEvent('passphrase-entered', {
                bubbles: true,
                composed: true,
                detail: { 
                    'passphrase' : code,
                    'text' : text
                }
            })
        );
        return (code === this.getAttribute('auth_token'));
    }
}
